const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

/**
 * The access token lives here rather than in a React context so that the plain
 * `api` object (and everything that imports it) can stay framework-agnostic.
 * `AuthProvider` keeps it in sync with the session.
 */
let authToken: string | null = null;

/** Called when a request comes back 401 so the session can be dropped. */
let unauthorizedHandler: (() => void) | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

export interface ApiFieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly details: ApiFieldError[];

  constructor(status: number, message: string, details: ApiFieldError[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

export type QueryValue = string | number | boolean | undefined | null | (string | number)[];

/** Serialises a query object, repeating keys for array values. */
export function buildQuery(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      value.forEach((entry) => search.append(key, String(entry)));
    } else {
      search.append(key, String(value));
    }
  }
  const query = search.toString();
  return query.length > 0 ? `?${query}` : '';
}

async function parse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const payload = text.length > 0 ? (JSON.parse(text) as unknown) : undefined;

  if (!response.ok) {
    // An expired/revoked token invalidates the whole session, not just this call.
    if (response.status === 401 && authToken) unauthorizedHandler?.();

    const envelope = payload as
      | { error?: { message?: string; details?: ApiFieldError[] } }
      | undefined;
    throw new ApiError(
      response.status,
      envelope?.error?.message ?? `Request failed with status ${response.status}`,
      envelope?.error?.details ?? [],
    );
  }

  return (payload as { data: T }).data;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  // Let the browser set the multipart boundary for uploads.
  if (!(init?.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  if (authToken) headers.set('Authorization', `Bearer ${authToken}`);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  } catch {
    throw new ApiError(0, 'Cannot reach the API. Is the backend running on port 8080?');
  }
  return parse<T>(response);
}

export const api = {
  get: <T>(path: string, params: Record<string, QueryValue> = {}): Promise<T> =>
    request<T>(`${path}${buildQuery(params)}`),

  post: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),

  put: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),

  delete: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),

  upload: <T>(path: string, file: File, fields: Record<string, string> = {}): Promise<T> => {
    const form = new FormData();
    form.append('file', file);
    for (const [key, value] of Object.entries(fields)) {
      form.append(key, value);
    }
    return request<T>(path, { method: 'POST', body: form });
  },
};

/** Normalises anything thrown by `api` into a message suitable for a toast. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.details.length > 0) {
      return error.details.map((detail) => detail.message).join(', ');
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}
